/**
 * Per-template (per-config) cross-tables API client (/api/configs/{id}/cross-tables).
 *
 * A config can carry its OWN cross-table values, uploaded as an X*.mdb workbook or a single
 * X*.csv. At generation/preview these OVERRIDE the org store per X-table for that config — an
 * X-table the config defines replaces the org's; X-tables it doesn't define fall back to the org.
 * Mirrors the org-level client in `orgXtables.ts`, scoped to one config. Mutations are admin-only
 * (server-enforced).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./client";

// ──────────────────────── Wire types ──────────────────────────────────────────

export interface ConfigCrossTableSummary {
  xtable: string;
  row_count: number;
  source_file: string;
  updated_at: string | null;
}

export interface ConfigCrossTableUploadResult {
  rows_written: number;
  tables: ConfigCrossTableSummary[];
}

// ──────────────────────── Hooks ───────────────────────────────────────────────

const key = (configId: string) => ["config-cross-tables", configId];

/** GET /api/configs/{id}/cross-tables — the X-tables this config overrides, with row counts. */
export function useConfigCrossTables(configId: string | undefined) {
  return useQuery({
    queryKey: key(configId ?? ""),
    enabled: !!configId,
    queryFn: async (): Promise<ConfigCrossTableSummary[]> => {
      const { data } = await api.get<ConfigCrossTableSummary[]>(
        `/configs/${configId}/cross-tables`,
      );
      return data;
    },
  });
}

/** POST /api/configs/{id}/cross-tables — upload an X*.mdb workbook or X*.csv (replace-per-table). */
export function useUploadConfigCrossTable(configId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File): Promise<ConfigCrossTableUploadResult> => {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post<ConfigCrossTableUploadResult>(
        `/configs/${configId}/cross-tables`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      return data;
    },
    onSuccess: (data) => {
      qc.setQueryData(key(configId), data.tables);
    },
  });
}

/** DELETE /api/configs/{id}/cross-tables/{xtable} — drop one X-table's per-config override. */
export function useDeleteConfigCrossTable(configId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (xtable: string): Promise<void> => {
      await api.delete(`/configs/${configId}/cross-tables/${encodeURIComponent(xtable)}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: key(configId) });
    },
  });
}
